import React, { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import Layout from "@/components/Layout";
import { Download, FileSpreadsheet, FileText } from "lucide-react";

export default function Reports() {
  const [summary, setSummary] = useState([]);
  const [months, setMonths] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, m] = await Promise.all([
        api.get("/reports/customers-summary"),
        api.get("/reports/pallets-by-month"),
      ]);
      setSummary(s.data);
      setMonths(m.data);
    })();
  }, []);

  const download = async (url, filename) => {
    const token = localStorage.getItem("nv_token");
    const res = await fetch(`${API}${url}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <Layout>
      <div className="px-6 md:px-10 py-8 max-w-[1400px] mx-auto">
        <div className="label-eyebrow" style={{ color: "#D4AF37" }}>Auswertung</div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mt-2">Reports</h1>

        <div className="grid md:grid-cols-2 gap-4 mt-8">
          <div className="card p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="label-eyebrow" style={{ color: "#D4AF37" }}>Export</div>
                <h2 className="text-xl font-semibold mt-2">Aufträge Excel</h2>
                <p className="text-sm text-[var(--text-3)] mt-1">Vollständige Liste aller Aufträge.</p>
              </div>
              <FileSpreadsheet size={22} strokeWidth={1.5} className="text-[var(--text-3)]" />
            </div>
            <button onClick={() => download("/reports/orders.xlsx", "auftraege.xlsx")} data-testid="report-xlsx-download" className="btn-gold mt-6 px-5 py-3 rounded-sm text-sm uppercase tracking-wider flex items-center gap-2">
              <Download size={16} strokeWidth={1.8} /> XLSX herunterladen
            </button>
          </div>

          <div className="card p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="label-eyebrow" style={{ color: "#D4AF37" }}>Export</div>
                <h2 className="text-xl font-semibold mt-2">Aufträge CSV</h2>
                <p className="text-sm text-[var(--text-3)] mt-1">Semikolon-getrennt, UTF-8 mit BOM.</p>
              </div>
              <FileText size={22} strokeWidth={1.5} className="text-[var(--text-3)]" />
            </div>
            <button onClick={() => download("/reports/orders.csv", "auftraege.csv")} data-testid="report-csv-download" className="btn-ghost mt-6 px-5 py-3 rounded-sm text-sm uppercase tracking-wider flex items-center gap-2">
              <Download size={16} strokeWidth={1.8} /> CSV herunterladen
            </button>
          </div>
        </div>

        <div className="mt-10">
          <h2 className="text-xl font-semibold mb-4">Aufträge pro Kunde</h2>
          <div className="card overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]">
              <thead>
                <tr className="border-b border-[var(--border)] text-[var(--text-3)] uppercase text-[11px] tracking-wider">
                  <th className="text-left py-3 px-4">Kunde</th>
                  <th className="text-right py-3 px-4">Aufträge</th>
                  <th className="text-right py-3 px-4">Paletten</th>
                  <th className="text-right py-3 px-4">Zugestellt</th>
                  <th className="text-right py-3 px-4">Umsatz (CHF)</th>
                </tr>
              </thead>
              <tbody>
                {summary.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-[var(--text-3)]">Keine Daten.</td></tr>}
                {summary.map((r) => (
                  <tr key={r.customer_id} className="border-b border-[var(--border)]">
                    <td className="py-3 px-4 text-[var(--text)]">{r.customer_name}</td>
                    <td className="py-3 px-4 font-mono text-right text-[var(--text)]">{r.orders}</td>
                    <td className="py-3 px-4 font-mono text-right text-[var(--text)]">{r.pallets}</td>
                    <td className="py-3 px-4 font-mono text-right text-[var(--text)]">{r.delivered}</td>
                    <td className="py-3 px-4 font-mono text-right text-[var(--text)]">{Number(r.revenue_chf || 0).toLocaleString("de-CH")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-10">
          <h2 className="text-xl font-semibold mb-4">Paletten pro Monat</h2>
          <div className="card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--border)] text-[var(--text-3)] uppercase text-[11px] tracking-wider">
                  <th className="text-left py-3 px-4">Monat</th>
                  <th className="text-right py-3 px-4">Aufträge</th>
                  <th className="text-right py-3 px-4">Paletten</th>
                </tr>
              </thead>
              <tbody>
                {months.length === 0 && <tr><td colSpan={3} className="text-center py-10 text-[var(--text-3)]">Keine Daten.</td></tr>}
                {months.map((r) => (
                  <tr key={r.month} className="border-b border-[var(--border)]">
                    <td className="py-3 px-4 font-mono text-[var(--text)]">{r.month}</td>
                    <td className="py-3 px-4 font-mono text-right text-[var(--text)]">{r.orders}</td>
                    <td className="py-3 px-4 font-mono text-right text-[var(--text)]">{r.pallets}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
